import { memo, useCallback } from "react";
import { Avatar, Button, Link, toast } from "@heroui/react";
import { ExternalLink } from "lucide-react";
import { useCompletedOrderCount, useUserProfile, useTemplateStats } from "@repo/query";
import { formatTime } from "@repo/utils/tools";
import {
  useAddToBlacklist,
  useBlacklistCheck,
  useRemoveFromBlacklist,
} from "../blacklist/use-blacklist";

export function QueryCard({
  title,
  loading,
  error,
  children,
  className,
}: {
  title: string;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-none border border-gray-100 bg-white p-5 shadow-sm ${className ?? ""}`}>
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        {loading && <span className="text-xs text-blue-500">加载中...</span>}
      </div>
      {error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : loading ? (
        <div className="space-y-2">
          <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-1/3 animate-pulse rounded bg-gray-100" />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export const UserInfoCard = memo(function UserInfoCard({
  uid,
  queryToken,
}: {
  uid: string;
  queryToken: number;
}) {
  const { data: userInfo, isLoading: loading, error } = useUserProfile(uid, queryToken);
  const errorMsg = error ? (error as { msg?: string }).msg || "用户信息查询失败" : null;

  if (loading || errorMsg) {
    return (
      <QueryCard title="用户信息" loading={loading} error={errorMsg} className="md:col-span-2">
        {null}
      </QueryCard>
    );
  }

  if (!userInfo) return null;

  return (
    <div className="relative md:col-span-2 overflow-hidden rounded-none border border-gray-100 shadow-sm">
      <img
        src={userInfo.banner_url || "/images/banners/user_banner_default.png"}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center"
        loading="lazy"
        decoding="async"
        fetchPriority="low"
      />
      <div className="absolute inset-0 bg-linear-to-r from-white from-0% via-white/90 via-45% to-white/10" />
      <div className="relative z-10 ml-4 p-5">
        <div className="flex justify-between">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Avatar>
                <Avatar.Image alt={userInfo.nickname} src={userInfo.avatar_url} />
                <Avatar.Fallback>
                  <div style={{ width: "100%", height: "100%", backgroundColor: "gray" }} />
                </Avatar.Fallback>
              </Avatar>
              <div>
                <p className="text-md font-medium text-gray-800">
                  {userInfo.nickname} -
                  {userInfo.real_name_verified ? (
                    <span className="ml-1 text-green-500">已实名</span>
                  ) : (
                    <span className="ml-1 text-red-500">未实名</span>
                  )}
                </p>
                <p className="text-sm text-gray-500">UID：{userInfo.uid}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              最后在线时间：
              {userInfo.last_seem !== 0
                ? formatTime(userInfo.last_seem, "YYYY-MM-DD HH:mm:ss")
                : "暂无数据"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export function TemplateStatsCard({ uid, queryToken }: { uid: string; queryToken: number }) {
  const { data: stats, isLoading: loading, error } = useTemplateStats(uid, queryToken);
  const errorMsg = error ? (error as { msg?: string }).msg || "模板统计查询失败" : null;

  return (
    <QueryCard title="模板发送/接收数" loading={loading} error={errorMsg}>
      {stats && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 p-3">
            <p className="text-gray-500">历史发货数</p>
            <p className="mt-1 text-2xl font-semibold text-blue-500">{stats.send_count}</p>
          </div>
          <div className="bg-gray-50 p-3">
            <p className="text-gray-500">历史收货数</p>
            <p className="mt-1 text-2xl font-semibold text-blue-500">{stats.receive_count}</p>
          </div>
          <div className="col-span-2 text-xs text-gray-500">
            注意：该数据仅显示截止1小时前的数据，并非实时，仅供参考
          </div>
        </div>
      )}
    </QueryCard>
  );
}

export function DelegationOrderCard({ uid, queryToken }: { uid: string; queryToken: number }) {
  const { data, isLoading: loading, error: queryError } = useCompletedOrderCount(uid, queryToken);
  const error = queryError ? (queryError as { msg?: string }).msg || "委托订单统计查询失败" : null;

  return (
    <QueryCard title="委托交易记录" loading={loading} error={error}>
      {data && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 p-3">
            <p className="text-gray-500">卖方完成订单数</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-700">{data.as_seller}</p>
          </div>
          <div className="bg-gray-50 p-3">
            <p className="text-gray-500">买方完成订单数</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-700">{data.as_buyer}</p>
          </div>
          <div className="col-span-2 text-xs text-gray-500">
            注意：该数据仅显示截止至昨日数据，并非实时，仅供参考
          </div>
        </div>
      )}
    </QueryCard>
  );
}

export function BlacklistHitCard({
  uid,
  queryToken,
  currentUserUid,
  onNavigateSelfBlacklist,
  onNavigatePublicBlacklist,
}: {
  uid: string;
  queryToken: number;
  currentUserUid?: string;
  onNavigateSelfBlacklist: () => void;
  onNavigatePublicBlacklist: (id: number, searchUid: string) => void;
}) {
  const { data: check, isLoading: loading, error: queryError } = useBlacklistCheck(uid, queryToken);
  const error = queryError ? (queryError as { msg?: string }).msg || "黑名单命中检查失败" : null;

  const handleSourceClick = useCallback(
    (id: number, searchUid: string) => {
      if (id === 0) {
        onNavigateSelfBlacklist();
        return;
      }
      onNavigatePublicBlacklist(id, searchUid);
    },
    [onNavigateSelfBlacklist, onNavigatePublicBlacklist],
  );

  // 拉黑 / 解除成功后黑名单域缓存失效，命中结果自动刷新
  const removeFromBlacklist = useRemoveFromBlacklist();
  const addToBlacklist = useAddToBlacklist();

  const handleRemoveBlacklist = useCallback(
    (targetUid: string) => {
      removeFromBlacklist.mutate(targetUid);
    },
    [removeFromBlacklist],
  );

  const handleAddBlacklist = useCallback(
    (targetUid: string) => {
      if (targetUid === currentUserUid) {
        toast("🥴 乐,我就知道你会这么干");
        return;
      }
      addToBlacklist.mutate({ uid: targetUid });
    },
    [currentUserUid, addToBlacklist],
  );

  return (
    <QueryCard title="公共黑名单状态" loading={loading} error={error}>
      {check && (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <div className="text-lg">
              {check.hit ? (
                <span className="font-medium">
                  该用户被 <span className="text-red-500">{check.source.length}</span> 个黑名单命中
                </span>
              ) : (
                <span className="font-medium text-green-600">暂无黑名单记录</span>
              )}
            </div>
            <div className="self-end">
              {check.source.some((item) => item.id === 0) ? (
                <Button size="sm" variant="danger-soft" onPress={() => handleRemoveBlacklist(uid)}>
                  解除拉黑
                </Button>
              ) : (
                <Button size="sm" variant="danger-soft" onPress={() => handleAddBlacklist(uid)}>
                  拉黑
                </Button>
              )}
            </div>
          </div>
          {check.hit && (
            <div className="flex flex-wrap items-center gap-2 text-gray-600">
              <span className="inline-flex text-xs">数据来源：</span>
              {check.source.map((item) => (
                <Link
                  key={item.id}
                  className="cursor-pointer text-xs leading-6 text-blue-500"
                  onPress={() => handleSourceClick(item.id, uid)}
                >
                  {item.name}
                  <ExternalLink size={12} className="ml-1 inline-block text-blue-500" />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </QueryCard>
  );
}

export function OfficalBlacklistCard({ uid, queryToken }: { uid: string; queryToken: number }) {
  const { data: userInfo, isLoading: loading, error } = useUserProfile(uid, queryToken);
  const errorMsg = error ? (error as { msg?: string }).msg || "黑名单命中检查失败" : null;

  return (
    <QueryCard title="标记机封禁状态" loading={loading} error={errorMsg}>
      <div className="space-y-2 text-sm">
        <p className="text-lg">
          {userInfo?.restricted.includes("template_protections") ? (
            <span className="font-medium text-red-500">已被官方封禁</span>
          ) : (
            <span className="font-medium text-green-600">正常</span>
          )}
        </p>
      </div>
    </QueryCard>
  );
}
